import { useEffect, useRef, useState, useCallback } from 'react';
import useMapMarkers from './useMapMarkers';
import { DEFAULT_MAP_OPTIONS } from '../constants/mapConstants';

/**
 * Custom hook for managing a Google Maps instance with markers and overlays.
 *
 * @param {Object} options
 * @param {Object} options.center - Initial map center { lat, lng }
 * @param {number} options.zoom - Initial map zoom level
 * @param {Array}  options.locations - Array of location/tile objects for markers
 * @param {Object} [options.mapOptions] - Additional map options to merge
 * @returns {Object} { mapRef, isLoaded, loadError, handleMapResize, portalContainer, tileData, closeOverlay }
 */
const useGoogleMap = ({
	center,
	zoom,
	locations = [],
	hoveredTileIndex,
	mapOptions
}) => {
	const mapRef = useRef(null);
	const [googleMap, setGoogleMap] = useState(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const [loadError, setLoadError] = useState(null);

	// Delegate marker management to dedicated hook
	const { customOverlayRef, portalContainer, tileData, markersUpdating } =
		useMapMarkers(googleMap, locations, isLoaded, hoveredTileIndex);

	/**
	 * Check if Google Maps script is already loaded.
	 * The script is loaded async/defer by ws-locations-data, so it may not
	 * be available immediately on mount. Poll as a fallback and also handle
	 * browser back-navigation (bfcache / pageshow).
	 */
	useEffect(() => {
		const checkGoogleMaps = () => {
			if (window.google && window.google.maps) {
				setIsLoaded(true);
				return true;
			}
			return false;
		};

		// Already available — nothing else to do
		if (checkGoogleMaps()) {
			return () => {};
		}

		// Poll until the async script finishes loading
		const pollInterval = setInterval(() => {
			if (checkGoogleMaps()) {
				clearInterval(pollInterval);
			}
		}, 100);

		// On bfcache restore (browser back button), re-check availability
		const handlePageShow = (event) => {
			if (event.persisted) {
				checkGoogleMaps();
			}
		};
		window.addEventListener('pageshow', handlePageShow);

		return () => {
			clearInterval(pollInterval);
			window.removeEventListener('pageshow', handlePageShow);
		};
	}, []);

	/**
	 * Initialize map once the script is loaded
	 */
	useEffect(() => {
		if (isLoaded && mapRef.current && !googleMap) {
			try {
				setGoogleMap(
					new window.google.maps.Map(mapRef.current, {
						center,
						zoom,
						...DEFAULT_MAP_OPTIONS,
						...mapOptions
					})
				);
			} catch (error) {
				setLoadError(error);
			}
		}
		// center and zoom are intentionally excluded from deps —
		// the map is created once; fitBounds (in useMapMarkers) handles
		// viewport adjustments based on actual marker positions.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, googleMap]);

	/**
	 * Trigger resize event when map becomes visible.
	 * Does NOT call setCenter — fitBounds is the sole viewport authority.
	 */
	const handleMapResize = useCallback(() => {
		if (googleMap && window.google) {
			window.google.maps.event.trigger(googleMap, 'resize');
		}
	}, [googleMap]);

	return {
		mapRef,
		isLoaded,
		isMapReady: isLoaded && !!googleMap,
		loadError,
		handleMapResize,
		portalContainer,
		tileData,
		markersUpdating,
		closeOverlay: () => {
			if (customOverlayRef.current?.close) {
				customOverlayRef.current.close();
			}
		}
	};
};

export default useGoogleMap;
